import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { lumi } from "@/lib/lumi"
import { useAuth } from "@/hooks/useAuth"
import {TicketCheck, Users, FileText, BarChart3, Clock, CheckCircle2, AlertTriangle, ArrowRight, Loader2, LogOut, TrendingUp, CalendarDays} from 'lucide-react'

export default function AdminDashboard() {
  const { user } = useAuth()
  const [tickets, setTickets] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const { list } = await lumi.entities.tickets.list({
        sort: { createdAt: -1 },
        limit: 200,
      })
      setTickets(list)
    } catch (err) {
      console.error("Erreur:", err)
    } finally {
      setLoading(false)
    }
  }

  const counts = {
    total: tickets.length,
    nouveau: tickets.filter((t) => t.status === "nouveau").length,
    en_cours: tickets.filter((t) => ["en_cours", "en_attente"].includes(t.status)).length,
    resolu: tickets.filter((t) => ["resolu", "ferme"].includes(t.status)).length,
    urgents: tickets.filter((t) => t.priority === "urgente" && !["resolu", "ferme", "annule"].includes(t.status)).length,
  }

  const recentTickets = tickets.slice(0, 8)

  const statusColor: Record<string, string> = {
    nouveau: "text-cyan-400 bg-cyan-500/10",
    en_attente: "text-amber-400 bg-amber-500/10",
    en_cours: "text-blue-400 bg-blue-500/10",
    attente_client: "text-orange-400 bg-orange-500/10",
    resolu: "text-emerald-400 bg-emerald-500/10",
    ferme: "text-slate-400 bg-slate-500/10",
    escalade: "text-red-400 bg-red-500/10",
    planifie: "text-purple-400 bg-purple-500/10",
    annule: "text-slate-500 bg-slate-600/10",
  }

  const statusLabels: Record<string, string> = {
    nouveau: "Nouveau",
    en_attente: "En attente",
    en_cours: "En cours",
    attente_client: "Att. client",
    resolu: "Resolu",
    ferme: "Ferme",
    escalade: "Escalade",
    planifie: "Planifie",
    annule: "Annule",
  }

  const handleLogout = async () => {
    await lumi.auth.signOut()
    window.location.href = "/"
  }

  return (
    <div className="min-h-screen pt-24 lg:pt-32 pb-20">
      <div className="section-padding">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8 animate-fade-in-up">
          <div>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-medium mb-2">
              Administration
            </span>
            <h1 className="text-2xl lg:text-3xl font-bold text-white">
              Back-office <span className="gradient-text">LJJ IT</span>
            </h1>
            <p className="text-slate-400 text-sm mt-1">Gestion des tickets et du site</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Link to="/admin/tickets" className="btn-primary text-sm !py-2.5">
              <TicketCheck className="w-4 h-4" />
              Gerer les tickets
            </Link>
            <Link to="/admin/rendez-vous" className="btn-secondary text-sm !py-2.5">
              <CalendarDays className="w-4 h-4" />
              Rendez-vous
            </Link>
            <Link to="/admin/pages" className="btn-secondary text-sm !py-2.5">
              <FileText className="w-4 h-4" />
              Gerer les pages
            </Link>
            <button onClick={handleLogout} className="btn-ghost text-sm !py-2.5 text-slate-500">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Stats */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
              {[
                { icon: BarChart3, label: "Total", value: counts.total, color: "from-cyan-500 to-blue-500" },
                { icon: AlertTriangle, label: "Nouveaux", value: counts.nouveau, color: "from-amber-500 to-orange-500" },
                { icon: Clock, label: "En cours", value: counts.en_cours, color: "from-blue-500 to-indigo-500" },
                { icon: CheckCircle2, label: "Resolus", value: counts.resolu, color: "from-emerald-500 to-teal-500" },
                { icon: TrendingUp, label: "Urgents", value: counts.urgents, color: "from-red-500 to-pink-500" },
              ].map((stat, i) => (
                <div
                  key={stat.label}
                  className="card !p-5 animate-fade-in-up"
                  style={{ animationDelay: `${0.05 * i}s` }}
                >
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center mb-3`}>
                    <stat.icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-2xl font-bold text-white">{stat.value}</div>
                  <div className="text-xs text-slate-400">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Recent tickets */}
            <div className="animate-fade-in-up" style={{ animationDelay: "0.3s" }}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white">Tickets recents</h2>
                <Link to="/admin/tickets" className="text-sm text-cyan-400 hover:underline flex items-center gap-1">
                  Voir tout <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>

              <div className="space-y-2">
                {recentTickets.map((ticket) => (
                  <Link
                    key={ticket._id}
                    to={`/admin/tickets/${ticket._id}`}
                    className="card !p-4 flex flex-col sm:flex-row sm:items-center gap-3 group hover:border-cyan-500/40"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColor[ticket.status] || ""}`}>
                          {statusLabels[ticket.status] || ticket.status}
                        </span>
                        <span className={`text-xs ${ticket.priority === "urgente" ? "text-red-400 font-semibold" : "text-slate-500"}`}>
                          {ticket.priority}
                        </span>
                      </div>
                      <h3 className="text-sm font-medium text-white truncate group-hover:text-cyan-400 transition-colors">
                        {ticket.subject}
                      </h3>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {ticket.clientName} &middot; {ticket.clientEmail} &middot; {new Date(ticket.createdAt).toLocaleDateString("fr-FR")}
                      </p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-cyan-400 shrink-0" />
                  </Link>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
